import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, UserPlus, Calendar as CalendarIcon, Package, Star, Grid3x3, List, LayoutGrid, Upload, Download, FileSpreadsheet, Eye, ArrowRightLeft, Search, AlertCircle, AlertTriangle } from 'lucide-react';
import { checkStudentRequiredFields, getEmptyFieldsTooltip } from '../utils/profileValidator';
import { format } from 'date-fns';
import StudentDetailView from './StudentDetailView';

// Helper function to extract error message from API response
const getErrorMessage = (error, fallback = 'Operation failed') => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (Array.isArray(detail)) {
      return detail.map(err => err.msg || JSON.stringify(err)).join(', ');
    } else if (typeof detail === 'object') {
      return detail.msg || JSON.stringify(detail);
    }
    return detail;
  }
  return fallback;
};

const StudentManagement = () => {
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [viewInfoDialog, setViewInfoDialog] = useState(false);
  const [selectedStudentInfo, setSelectedStudentInfo] = useState(null);
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [transferDialog, setTransferDialog] = useState(false);
  const [selectedStudentForTransfer, setSelectedStudentForTransfer] = useState(null);
  const [transferCenterId, setTransferCenterId] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [transferConfirmation, setTransferConfirmation] = useState('');
  const [centers, setCenters] = useState([]);
  const [viewingStudentId, setViewingStudentId] = useState(null);

  // Helper function to safely parse and validate dates
  const isValidDate = (dateValue) => {
    if (!dateValue) return false;
    const date = new Date(dateValue);
    return date instanceof Date && !isNaN(date.getTime());
  };

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    gk_code: '',
    date_of_birth: null,
    date_of_joining: null,
    module: '',
    group: '',
    status: 'active',
    batch_id: '',
    gender: '',
    father_name: '',
    mother_name: '',
    contact_number: '',
    alternate_contact_number: '',
    full_address: '',
    school_name: '',
    grade: '',
    student_mode: '',
    payment_term: '',
    request_kit: false,
    kit_notes: ''
  });
  const [kitRequestDialog, setKitRequestDialog] = useState(false);
  const [starRatingDialog, setStarRatingDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [kitNotes, setKitNotes] = useState('');
  const [preferredDeliveryAddress, setPreferredDeliveryAddress] = useState('center');
  const [starRating, setStarRating] = useState(0);
  const [starComment, setStarComment] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list', 'grid', 'block'
  const [searchQuery, setSearchQuery] = useState('');
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    loadStudents();
    loadBatches();
    loadCenters();
  }, []);

  const loadStudents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users?role=student&center_id=${user.center_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data);
    } catch (error) {
      console.error('Failed to load students:', error);
      toast.error('Failed to load students');
    }
  };

  // Filter students based on search query
  const filteredStudents = students.filter(student => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      student.name?.toLowerCase().includes(query) ||
      student.email?.toLowerCase().includes(query) ||
      student.gk_code?.toLowerCase().includes(query) ||
      student.module?.toLowerCase().includes(query) ||
      student.contact_number?.includes(query)
    );
  });

  // Count students with missing details
  const studentsWithMissingDetails = useMemo(() => {
    return students.filter(student => {
      const validation = checkStudentRequiredFields(student);
      return validation.hasEmptyFields;
    }).length;
  }, [students]);

  const loadBatches = async () => {
    try {
      const response = await axios.get(`${API}/batches`);
      setBatches(response.data);
    } catch (error) {
      console.error('Failed to load batches');
    }
  };

  const loadCenters = async () => {
    try {
      const response = await axios.get(`${API}/centers`);
      setCenters(response.data.filter(c => c.id !== user.center_id)); // Exclude current center
    } catch (error) {
      console.error('Failed to load centers');
    }
  };

  const handleTransferStudent = async () => {
    if (!selectedStudentForTransfer || !transferCenterId || !transferReason.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Security check - user must type student name exactly
    if (transferConfirmation !== selectedStudentForTransfer.name) {
      toast.error('Student name confirmation does not match. Please type the exact name.');
      return;
    }

    try {
      await axios.post(`${API}/transfer-requests`, {
        student_id: selectedStudentForTransfer.id,
        to_center_id: transferCenterId,
        reason: transferReason
      });
      
      toast.success('Transfer request submitted successfully. Waiting for admin approval.');
      setTransferDialog(false);
      setSelectedStudentForTransfer(null);
      setTransferCenterId('');
      setTransferReason('');
      setTransferConfirmation('');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to submit transfer request'));
    }
  };

  const calculateAge = (dob) => {
    if (!dob || !isValidDate(dob)) return null;
    return Math.floor((new Date() - new Date(dob)) / (365.25 * 24 * 60 * 60 * 1000));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        role: 'student',
        center_id: user.center_id
      };

      let createdStudent = null;
      
      if (editingStudent) {
        await axios.put(`${API}/users/${editingStudent.id}`, submitData);
        toast.success('Student updated successfully');
      } else {
        const response = await axios.post(`${API}/auth/register`, submitData);
        createdStudent = response.data;
        toast.success('Student added successfully');

        // Create kit request if checkbox is checked
        if (formData.request_kit && createdStudent) {
          try {
            const token = localStorage.getItem('token');
            await axios.post(
              `${API}/kit-requests`,
              {
                student_id: createdStudent.id,
                request_notes: formData.kit_notes || 'Kit requested for new student'
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Kit request created successfully');
          } catch (kitError) {
            console.error('Kit request error:', kitError);
            toast.warning('Student created but kit request failed');
          }
        }
      }
      
      setIsDialogOpen(false);
      setFormData({
        email: '',
        password: '',
        name: '',
        gk_code: '',
        date_of_birth: null,
        date_of_joining: null,
        module: '',
        group: '',
        status: 'active',
        batch_id: '',
        gender: '',
        father_name: '',
        mother_name: '',
        contact_number: '',
        alternate_contact_number: '',
        full_address: '',
        school_name: '',
        grade: '',
        student_mode: '',
        payment_term: '',
        request_kit: false,
        kit_notes: ''
      });
      setEditingStudent(null);
      loadStudents();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Operation failed'));
    }
  };

  const handleEdit = (student) => {
    setEditingStudent(student);
    setFormData({
      email: student.email,
      password: '',
      name: student.name,
      gk_code: student.gk_code || '',
      date_of_birth: student.date_of_birth || null,
      date_of_joining: student.date_of_joining || null,
      module: student.module || '',
      group: student.group || '',
      status: student.status || 'active',
      batch_id: student.batch_id || '',
      gender: student.gender || '',
      father_name: student.father_name || '',
      mother_name: student.mother_name || '',
      contact_number: student.contact_number || '',
      alternate_contact_number: student.alternate_contact_number || '',
      full_address: student.full_address || '',
      school_name: student.school_name || '',
      grade: student.grade || '',
      student_mode: student.student_mode || '',
      payment_term: student.payment_term || '',
      request_kit: false,
      kit_notes: ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (studentId) => {
    if (!window.confirm('Are you sure you want to delete this student?')) return;
    try {
      await axios.delete(`${API}/users/${studentId}`);
      toast.success('Student deleted successfully');
      loadStudents();
    } catch (error) {
      toast.error('Failed to delete student');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/export/excel?role=student`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `students_export.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Students exported successfully');
    } catch (error) {
      toast.error('Failed to export students');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/sample-template/excel?role=student`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `student_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Please select a file to import');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/users/import/excel?role=student&update_existing=${updateExisting}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.errors && response.data.errors.length > 0) {
        setImportErrors(response.data.errors);
        setShowErrorDialog(true);
        toast.warning(`Imported ${response.data.imported_count} student(s) with ${response.data.error_count} errors. Click to view details.`);
      } else {
        toast.success(response.data.message);
      }
      
      loadStudents();
      setImportDialog(false);
      setImportFile(null);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to import students'));
    }
  };

  if (!user.center_id) {
    return (
      <Card className="p-6 bg-white shadow-lg border-0">
        <div className="text-center py-12">
          <UserPlus className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Center Assigned</h3>
          <p className="text-gray-600">You need to be assigned to a center to manage students.</p>
        </div>
      </Card>
    );
  }

  // If viewing a student's details, show the detail view
  if (viewingStudentId) {
    const studentData = students.find(s => s.id === viewingStudentId);
    return (
      <StudentDetailView 
        studentId={viewingStudentId} 
        studentData={studentData}
        onBack={() => setViewingStudentId(null)}
        onEdit={(student) => {
          setViewingStudentId(null);
          handleEdit(student);
        }}
      />
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="student-management">
      <Card className="p-6 bg-white shadow-lg border-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <h3 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Student Management
          </h3>
          
          {/* Search Bar */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by name, email, GK code, module..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 w-full"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Import/Export Buttons */}
            <div className="flex items-center gap-2">
              <Button
                onClick={handleDownloadTemplate}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                title="Download Template"
              >
                <FileSpreadsheet className="w-4 h-4" />
                <span className="hidden sm:inline">Template</span>
              </Button>
              
              <Button
                onClick={() => setImportDialog(true)}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                title="Import from Excel"
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline">Import</span>
              </Button>
              
              <Button
                onClick={handleExport}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                title="Export to Excel"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Export</span>
              </Button>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-white rounded-lg p-1 shadow-sm border">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-all ${
                  viewMode === 'list' 
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-md' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="List View"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-all ${
                  viewMode === 'grid' 
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-md' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Grid View"
              >
                <Grid3x3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('block')}
                className={`p-2 rounded transition-all ${
                  viewMode === 'block' 
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-md' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Block View"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="flex items-center space-x-2 bg-gradient-to-r from-emerald-500 to-teal-600" data-testid="add-student-button">
                  <Plus className="w-4 h-4" />
                  <span>Add Student</span>
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="student-dialog">
              <DialogHeader>
                <DialogTitle>{editingStudent ? 'Edit Student' : 'Add New Student'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="student-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="student-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password">Password {editingStudent && '(leave blank to keep current)'}</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required={!editingStudent}
                      data-testid="student-password-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="gk_code">GK Code</Label>
                    <Input
                      id="gk_code"
                      value={formData.gk_code}
                      onChange={(e) => setFormData({ ...formData, gk_code: e.target.value })}
                      placeholder="e.g., GK001"
                      data-testid="student-gk-code-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="module">Module</Label>
                    <Select value={formData.module} onValueChange={(value) => setFormData({ ...formData, module: value })}>
                      <SelectTrigger data-testid="student-module-select">
                        <SelectValue placeholder="Select module" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="toddler">Toddler</SelectItem>
                        <SelectItem value="junior">Junior</SelectItem>
                        <SelectItem value="senior">Senior</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="group">Group</Label>
                    <Select value={formData.group} onValueChange={(value) => setFormData({ ...formData, group: value })}>
                      <SelectTrigger data-testid="student-group-select">
                        <SelectValue placeholder="Select group" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="beginner">Beginner</SelectItem>
                        <SelectItem value="intermediate">Intermediate</SelectItem>
                        <SelectItem value="advanced">Advanced</SelectItem>
                        <SelectItem value="knightstar_i">KnightStar I</SelectItem>
                        <SelectItem value="knightstar_ii">KnightStar II</SelectItem>
                        <SelectItem value="knightstar_iii">KnightStar III</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    {/* Empty div for grid alignment */}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date_of_birth">Date of Birth</Label>
                    <Input
                      id="date_of_birth"
                      type="date"
                      value={isValidDate(formData.date_of_birth) ? new Date(formData.date_of_birth).toISOString().split('T')[0] : ''}
                      onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value ? new Date(e.target.value).toISOString() : null })}
                      data-testid="student-dob-input"
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="date_of_joining">Date of Joining</Label>
                    <Input
                      id="date_of_joining"
                      type="date"
                      value={isValidDate(formData.date_of_joining) ? new Date(formData.date_of_joining).toISOString().split('T')[0] : ''}
                      onChange={(e) => setFormData({ ...formData, date_of_joining: e.target.value ? new Date(e.target.value).toISOString() : null })}
                      data-testid="student-doj-input"
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="gender">Gender</Label>
                    <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                      <SelectTrigger data-testid="student-gender-select">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="grade">Grade</Label>
                    <Input
                      id="grade"
                      value={formData.grade}
                      onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                      placeholder="e.g., 5th Grade"
                      data-testid="student-grade-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="father_name">Father's Name</Label>
                    <Input
                      id="father_name"
                      value={formData.father_name}
                      onChange={(e) => setFormData({ ...formData, father_name: e.target.value })}
                      placeholder="Father's full name"
                      data-testid="student-father-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="mother_name">Mother's Name</Label>
                    <Input
                      id="mother_name"
                      value={formData.mother_name}
                      onChange={(e) => setFormData({ ...formData, mother_name: e.target.value })}
                      placeholder="Mother's full name"
                      data-testid="student-mother-name-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_number">Contact Number</Label>
                    <Input
                      id="contact_number"
                      value={formData.contact_number}
                      onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                      placeholder="Primary contact number"
                      data-testid="student-contact-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="alternate_contact_number">Alternate Contact Number</Label>
                    <Input
                      id="alternate_contact_number"
                      value={formData.alternate_contact_number}
                      onChange={(e) => setFormData({ ...formData, alternate_contact_number: e.target.value })}
                      placeholder="Alternate contact number"
                      data-testid="student-alternate-contact-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="school_name">School Name</Label>
                  <Input
                    id="school_name"
                    value={formData.school_name}
                    onChange={(e) => setFormData({ ...formData, school_name: e.target.value })}
                    placeholder="School name"
                    data-testid="student-school-input"
                  />
                </div>

                <div>
                  <Label htmlFor="full_address">Full Address</Label>
                  <textarea
                    id="full_address"
                    value={formData.full_address}
                    onChange={(e) => setFormData({ ...formData, full_address: e.target.value })}
                    placeholder="Complete residential address"
                    className="flex min-h-[80px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="student-address-input"
                    rows="3"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="batch">Batch (Optional)</Label>
                    <Select value={formData.batch_id} onValueChange={(value) => setFormData({ ...formData, batch_id: value })}>
                      <SelectTrigger data-testid="student-batch-select">
                        <SelectValue placeholder="Select batch" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Batch</SelectItem>
                        {batches.map(batch => (
                          <SelectItem key={batch.id} value={batch.id}>{batch.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="student_mode">Student Mode</Label>
                    <Select value={formData.student_mode} onValueChange={(value) => setFormData({ ...formData, student_mode: value })}>
                      <SelectTrigger data-testid="student-mode-select">
                        <SelectValue placeholder="Select mode" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="online">Online</SelectItem>
                        <SelectItem value="offline">Offline</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="payment_term">Payment Term</Label>
                    <Select value={formData.payment_term} onValueChange={(value) => setFormData({ ...formData, payment_term: value })}>
                      <SelectTrigger data-testid="payment-term-select">
                        <SelectValue placeholder="Select payment term" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="half_yearly">Half Yearly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                        <SelectItem value="foc">FOC (Free of Charge)</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.payment_term === 'foc' && (
                      <p className="text-sm text-amber-600 mt-1 flex items-center gap-1">
                        <span>⚠️</span> FOC students require admin approval
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger data-testid="student-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="on_break">On Break</SelectItem>
                        <SelectItem value="non_active">Non Active</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {!editingStudent && (
                  <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="request_kit"
                        checked={formData.request_kit}
                        onChange={(e) => setFormData({ ...formData, request_kit: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <Label htmlFor="request_kit" className="cursor-pointer font-medium text-green-900">
                        Order Kit for this Student
                      </Label>
                    </div>
                    {formData.request_kit && (
                      <div>
                        <Label htmlFor="kit_notes" className="text-sm text-green-800">Kit Request Notes (Optional)</Label>
                        <Input
                          type="text"
                          id="kit_notes"
                          value={formData.kit_notes}
                          onChange={(e) => setFormData({ ...formData, kit_notes: e.target.value })}
                          placeholder="Add any special notes for the kit request..."
                          className="mt-1 border-green-300"
                        />
                      </div>
                    )}
                  </div>
                )}

                <Button type="submit" className="w-full" data-testid="submit-student-button">
                  {editingStudent ? 'Update Student' : 'Add Student'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Alert Notification for Missing Details */}
        {studentsWithMissingDetails > 0 && (
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg shadow-sm mb-4">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="text-amber-900 font-semibold text-sm mb-1">
                  Incomplete Student Profiles
                </h4>
                <p className="text-amber-800 text-sm">
                  <span className="font-bold">{studentsWithMissingDetails}</span> {studentsWithMissingDetails === 1 ? 'student has' : 'students have'} missing required details. 
                  Please update their profiles to ensure complete records. Look for the <AlertCircle className="w-3 h-3 text-amber-600 inline mx-1" /> icon next to student names.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Email</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">GK Code</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Module</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Age</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Status</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredStudents.map((student, idx) => (
                  <tr key={student.id} className={`hover:bg-gray-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} data-testid={`student-${student.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold">
                          {student.name?.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">{student.name}</span>
                          {(() => {
                            const validation = checkStudentRequiredFields(student);
                            if (validation.hasEmptyFields) {
                              return (
                                <div className="relative group">
                                  <AlertCircle className="w-4 h-4 text-amber-500" />
                                  <div className="absolute left-0 top-6 hidden group-hover:block bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                                    {getEmptyFieldsTooltip(validation.emptyFields)}
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{student.email}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">{student.gk_code || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 capitalize">{student.module || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {student.date_of_birth ? `${calculateAge(student.date_of_birth)} yrs` : 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        student.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                        student.status === 'on_break' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {student.status === 'on_break' ? 'On Break' : student.status === 'non_active' ? 'Non Active' : student.status || 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center">
                        <button onClick={() => setViewingStudentId(student.id)} className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors" title="View Details">
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Grid View */}
        {viewMode === 'grid' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredStudents.map(student => (
              <Card key={student.id} className="p-5 border hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-gray-50" data-testid={`student-${student.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                        {student.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900">{student.name}</h4>
                        <p className="text-sm text-gray-500">{student.email}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2 mb-3">
                  {student.gk_code && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">GK Code:</span>
                      <span className="font-semibold text-gray-900">{student.gk_code}</span>
                    </div>
                  )}
                  {student.module && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Module:</span>
                      <span className="font-semibold text-gray-900 capitalize">{student.module}</span>
                    </div>
                  )}
                  {student.date_of_birth && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Age:</span>
                      <span className="font-semibold text-gray-900">{calculateAge(student.date_of_birth)} years</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                    student.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                    student.status === 'on_break' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {student.status === 'on_break' ? 'On Break' : student.status === 'non_active' ? 'Non Active' : 'Active'}
                  </span>
                </div>

                <div className="flex justify-center mt-4">
                  <Button onClick={() => setViewingStudentId(student.id)} variant="outline" size="sm" className="w-full" title="View Details">
                    <Eye className="w-4 h-4 mr-2" />
                    View Details
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Block View */}
        {viewMode === 'block' && (
          <div className="grid grid-cols-1 gap-3">
            {filteredStudents.map(student => (
              <Card key={student.id} className="p-4 border hover:shadow-md transition-all duration-200" data-testid={`student-${student.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                      {student.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 text-lg">{student.name}</h4>
                      <p className="text-sm text-gray-500">{student.email}</p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {student.gk_code && <span className="text-xs bg-gray-100 px-2 py-1 rounded">GK: {student.gk_code}</span>}
                        {student.module && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded capitalize">{student.module}</span>}
                        {student.date_of_birth && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">{calculateAge(student.date_of_birth)} yrs</span>}
                        <span className={`text-xs px-2 py-1 rounded ${
                          student.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                          student.status === 'on_break' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {student.status === 'on_break' ? 'On Break' : student.status === 'non_active' ? 'Non Active' : student.status || 'Active'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button onClick={() => setViewingStudentId(student.id)} variant="outline" size="sm">
                      <Eye className="w-4 h-4 mr-2" />
                      View Details
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {filteredStudents.length === 0 && students.length === 0 && (
          <p className="text-gray-500 text-center py-8">No students in your center yet. Add your first student to get started!</p>
        )}
        
        {filteredStudents.length === 0 && students.length > 0 && (
          <p className="text-gray-500 text-center py-8">No students found matching "{searchQuery}". Try a different search term.</p>
        )}
      </Card>

      {/* Star Rating Dialog */}
      <Dialog open={starRatingDialog} onOpenChange={setStarRatingDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Give Stars to Student</DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Student: <span className="font-semibold text-gray-900">{selectedStudent.name}</span></p>
                <p className="text-sm text-gray-600">GK Code: <span className="font-semibold text-gray-900">{selectedStudent.gk_code || 'N/A'}</span></p>
              </div>

              <div>
                <Label>Rating (1-5 stars)</Label>
                <div className="flex items-center gap-2 mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setStarRating(star)}
                      className="focus:outline-none transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-8 h-8 ${
                          star <= starRating
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'fill-none text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    {starRating > 0 ? `${starRating} star${starRating > 1 ? 's' : ''}` : 'Select rating'}
                  </span>
                </div>
              </div>

              <div>
                <Label htmlFor="star_comment">Comment (Optional)</Label>
                <Input
                  id="star_comment"
                  value={starComment}
                  onChange={(e) => setStarComment(e.target.value)}
                  placeholder="Add a comment about the student's performance..."
                />
              </div>

              <Button 
                onClick={async () => {
                  if (starRating === 0) {
                    toast.error('Please select a star rating');
                    return;
                  }
                  
                  try {
                    const token = localStorage.getItem('token');
                    await axios.post(
                      `${API}/stars`,
                      {
                        student_id: selectedStudent.id,
                        stars: starRating,
                        comment: starComment || ''
                      },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    toast.success('Star rating submitted successfully');
                    setStarRatingDialog(false);
                    setSelectedStudent(null);
                    setStarRating(0);
                    setStarComment('');
                  } catch (error) {
                    toast.error(getErrorMessage(error, 'Failed to submit star rating'));
                  }
                }}
                className="w-full"
                disabled={starRating === 0}
              >
                Submit Rating
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Kit Request Dialog */}
      <Dialog open={kitRequestDialog} onOpenChange={setKitRequestDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Kit for Student</DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Student: <span className="font-semibold text-gray-900">{selectedStudent.name}</span></p>
                <p className="text-sm text-gray-600">GK Code: <span className="font-semibold text-gray-900">{selectedStudent.gk_code || 'N/A'}</span></p>
              </div>

              <div>
                <Label className="text-sm font-semibold mb-2 block">Preferred Delivery Address</Label>
                <div className="space-y-2">
                  <div 
                    className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      preferredDeliveryAddress === 'center' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setPreferredDeliveryAddress('center')}
                  >
                    <div className="flex items-center space-x-2">
                      <input 
                        type="radio" 
                        checked={preferredDeliveryAddress === 'center'}
                        onChange={() => setPreferredDeliveryAddress('center')}
                      />
                      <span className="font-medium text-sm">Center Address</span>
                    </div>
                  </div>

                  <div 
                    className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      preferredDeliveryAddress === 'student' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setPreferredDeliveryAddress('student')}
                  >
                    <div className="flex items-center space-x-2">
                      <input 
                        type="radio" 
                        checked={preferredDeliveryAddress === 'student'}
                        onChange={() => setPreferredDeliveryAddress('student')}
                      />
                      <span className="font-medium text-sm">Student Home Address</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Note: Final delivery address will be confirmed by admin
                </p>
              </div>

              <div>
                <Label htmlFor="kit_notes">Notes (Optional)</Label>
                <Input
                  id="kit_notes"
                  value={kitNotes}
                  onChange={(e) => setKitNotes(e.target.value)}
                  placeholder="Add any special instructions or notes..."
                />
              </div>

              <Button 
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    await axios.post(
                      `${API}/kit-requests`,
                      {
                        student_id: selectedStudent.id,
                        request_notes: kitNotes || 'Kit requested by teacher',
                        preferred_delivery_address: preferredDeliveryAddress
                      },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    toast.success('Kit request created successfully');
                    setKitRequestDialog(false);
                    setSelectedStudent(null);
                    setKitNotes('');
                    setPreferredDeliveryAddress('center');
                  } catch (error) {
                    toast.error(getErrorMessage(error, 'Failed to create kit request'));
                  }
                }}
                className="w-full"
              >
                Submit Kit Request
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transfer Student Dialog */}
      <Dialog open={transferDialog} onOpenChange={(open) => {
        setTransferDialog(open);
        if (!open) {
          setSelectedStudentForTransfer(null);
          setTransferCenterId('');
          setTransferReason('');
          setTransferConfirmation('');
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5" />
              Transfer Student to Another Center
            </DialogTitle>
          </DialogHeader>
          {selectedStudentForTransfer && (
            <div className="space-y-4">
              {/* Warning Banner */}
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-semibold mb-2">⚠️ Important Notice</p>
                <p className="text-xs text-red-700">
                  This action requires admin approval. The student will be transferred to the selected center only after approval.
                </p>
              </div>

              {/* Student Info */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-600">Student Name:</p>
                <p className="font-bold text-gray-900 text-lg">{selectedStudentForTransfer.name}</p>
                <p className="text-sm text-gray-600 mt-2">GK Code: <span className="font-semibold">{selectedStudentForTransfer.gk_code || 'N/A'}</span></p>
                <p className="text-sm text-gray-600">Current Center: <span className="font-semibold">{selectedStudentForTransfer.center_name || 'N/A'}</span></p>
              </div>

              {/* Transfer To Center */}
              <div>
                <Label htmlFor="transfer_center" className="text-sm font-semibold">Transfer To Center *</Label>
                <Select value={transferCenterId} onValueChange={setTransferCenterId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select target center" />
                  </SelectTrigger>
                  <SelectContent>
                    {centers.map(center => (
                      <SelectItem key={center.id} value={center.id}>
                        {center.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Reason */}
              <div>
                <Label htmlFor="transfer_reason" className="text-sm font-semibold">Reason for Transfer *</Label>
                <textarea
                  id="transfer_reason"
                  value={transferReason}
                  onChange={(e) => setTransferReason(e.target.value)}
                  placeholder="Please provide a detailed reason for this transfer request..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mt-1 min-h-[80px] focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>

              {/* Security Confirmation */}
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                <Label htmlFor="transfer_confirmation" className="text-sm font-semibold text-yellow-900 mb-2 block">
                  🔒 Security Confirmation
                </Label>
                <p className="text-xs text-yellow-800 mb-3">
                  To confirm this transfer, please type the student's name exactly: <span className="font-bold text-yellow-900">{selectedStudentForTransfer.name}</span>
                </p>
                <Input
                  id="transfer_confirmation"
                  value={transferConfirmation}
                  onChange={(e) => setTransferConfirmation(e.target.value)}
                  placeholder="Type student name here"
                  className="border-yellow-400 focus:ring-yellow-500"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={() => {
                    setTransferDialog(false);
                    setSelectedStudentForTransfer(null);
                    setTransferCenterId('');
                    setTransferReason('');
                    setTransferConfirmation('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleTransferStudent}
                  disabled={
                    !transferCenterId ||
                    !transferReason.trim() ||
                    transferConfirmation !== selectedStudentForTransfer.name
                  }
                  className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Submit Transfer Request
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Errors Dialog */}
      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Errors ({importErrors.length})</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-gray-600 mb-4">
              The following rows had errors and were not imported. Please fix these issues in your Excel file and try again.
            </p>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {importErrors.map((error, index) => (
                <div key={index} className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                  {error}
                </div>
              ))}
            </div>
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded">
              <h4 className="font-semibold text-blue-900 mb-2">Common Issues:</h4>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>Check that required fields (Name*, Email*, Password*) are filled</li>
                <li>Verify email addresses don't already exist in the system</li>
                <li>Date format should be YYYY-MM-DD (e.g., 2015-05-15)</li>
                <li>Module values: toddler, junior, senior (lowercase)</li>
                <li>Group values: beginner, intermediate, knightstar_i, knightstar_ii</li>
                <li>Gender values: male, female, other</li>
                <li>Status values: active, on_break, non_active</li>
              </ul>
            </div>
            <Button onClick={() => setShowErrorDialog(false)} className="w-full mt-4">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Student Info Dialog */}
      <Dialog open={viewInfoDialog} onOpenChange={setViewInfoDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Student Information</DialogTitle>
          </DialogHeader>
          {selectedStudentInfo && (
            <div className="space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Personal Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="font-semibold">{selectedStudentInfo.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-semibold">{selectedStudentInfo.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">GK Code</p>
                    <p className="font-semibold">{selectedStudentInfo.gk_code || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Gender</p>
                    <p className="font-semibold capitalize">{selectedStudentInfo.gender || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Date of Birth</p>
                    <p className="font-semibold">
                      {selectedStudentInfo.date_of_birth ? new Date(selectedStudentInfo.date_of_birth).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Age</p>
                    <p className="font-semibold">
                      {selectedStudentInfo.date_of_birth ? `${calculateAge(selectedStudentInfo.date_of_birth)} years` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Father's Name</p>
                    <p className="font-semibold">{selectedStudentInfo.father_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Mother's Name</p>
                    <p className="font-semibold">{selectedStudentInfo.mother_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Contact Number</p>
                    <p className="font-semibold">{selectedStudentInfo.contact_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Alternate Contact</p>
                    <p className="font-semibold">{selectedStudentInfo.alternate_contact_number || 'N/A'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-gray-600">Address</p>
                    <p className="font-semibold">{selectedStudentInfo.full_address || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Academic Information */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Academic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Module</p>
                    <p className="font-semibold capitalize">{selectedStudentInfo.module || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Group</p>
                    <p className="font-semibold capitalize">{selectedStudentInfo.group || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">School Name</p>
                    <p className="font-semibold">{selectedStudentInfo.school_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Grade</p>
                    <p className="font-semibold">{selectedStudentInfo.grade || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Date of Joining</p>
                    <p className="font-semibold">
                      {selectedStudentInfo.date_of_joining ? new Date(selectedStudentInfo.date_of_joining).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Student Mode</p>
                    <p className="font-semibold capitalize">{selectedStudentInfo.student_mode || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Payment Term</p>
                    <p className="font-semibold capitalize">{selectedStudentInfo.payment_term || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <span className={`px-3 py-1 text-sm rounded-full ${
                      selectedStudentInfo.status === 'active' ? 'bg-green-100 text-green-800' :
                      selectedStudentInfo.status === 'on_break' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {selectedStudentInfo.status === 'on_break' ? 'On Break' : 
                       selectedStudentInfo.status === 'non_active' ? 'Non Active' : 'Active'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end mt-4">
            <Button onClick={() => setViewInfoDialog(false)} variant="outline">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialog} onOpenChange={setImportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Students from Excel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Excel File</Label>
              <div className="mt-2">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {importFile && (
                <p className="text-sm text-gray-600 mt-2">
                  Selected: {importFile.name}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-md">
              <input
                type="checkbox"
                checked={updateExisting}
                onChange={(e) => setUpdateExisting(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                id="update-existing-checkbox"
              />
              <Label htmlFor="update-existing-checkbox" className="text-sm cursor-pointer">
                Update existing students if email matches
              </Label>
            </div>

            <div className="bg-blue-50 p-3 rounded-md">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> If "Update existing" is checked, students with matching email addresses will be updated. 
                Otherwise, duplicate emails will be skipped.
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              <Button onClick={() => { setImportDialog(false); setImportFile(null); }} variant="outline">
                Cancel
              </Button>
              <Button onClick={handleImport} disabled={!importFile} className="bg-blue-600 hover:bg-blue-700">
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentManagement;
